import { Box } from '@mui/material';
import styled, { css } from 'styled-components';

/**
 * The order title and the close button, and deliberately nothing else — the status chip and the preorder
 * badge scroll away with the rest. In the details panel this is the one row that stays put, so the way
 * out is never scrolled off; hence the opaque background the content passes under, and the shadow it
 * takes on once there is content under it. `$sticky` is off on the full page, which scrolls as one piece
 * and has no close button to keep in reach.
 *
 * Its containing block has to be the column that spans the whole scrolled content — a sticky element
 * cannot outlast its parent's box — so it is rendered as a direct child of `OrderDetailsView`'s layout
 * rather than inside a header wrapper of its own.
 */
export const PanelTopBar = styled.div<{ $sticky: boolean; $raised: boolean }>`
  display: flex;
  align-items: flex-start;
  /* Keeps the close button at the right edge whether or not there is a title beside it: while the order
     is still loading, the button is this row's only child. */
  justify-content: flex-end;
  gap: 8px;

  ${({ theme, $sticky, $raised }) =>
    $sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: ${theme.palette.background.paper};
      /* Reaches into the panel scroller's 16px side padding and pays it back as its own, so the bar and
         its shadow span the paper edge to edge rather than reading as a card floating inside it. The
         padding at the top is what keeps the title off the paper's edge — the scroller has none. */
      margin: 0 -16px;
      padding: 12px 16px 8px;
      transition: box-shadow 150ms ${theme.transitions.easing.easeOut};
      ${$raised &&
      css`
        box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2);
      `}
    `}
`;

export const CustomerDetailsContainer = styled(Box)`
  display: flex;
  flex-flow: column;
  margin-bottom: 5px;
`;

/** A stacked block of lines inside an `InformationRow` — a customer's name, email and phone. */
export const InformationText = styled.div`
  flex-direction: column;
`;

// Direction is deliberately left as a row: OrderEstimates and OrderAdditionalDetails use this for
// inline "<label> <value>" pairs, where stacking would read wrong. OrderDetails.tsx has its own
// stacking variant for the label/value rows that actually need it.
export const InformationRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 8px 0px 8px;
`;

/**
 * A card's label/value rows. `auto` rather than a fixed label width: the column sizes itself to the
 * longest label present, so values line up at any width without a number a translation could
 * outgrow. For rows whose values are short and uniform — times, payment facts. The free-text rows
 * elsewhere (address, email, instructions) still stack, because they need the width.
 */
export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 12px;
  align-items: baseline;
`;

export const FieldLabel = styled.div`
  font-weight: bold;
  overflow-wrap: anywhere;
`;

export const FieldValue = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
`;
